/**
 * /earthworks/tickets/:id — one field ticket (EW-5, GET /earthworks/tickets/:id).
 *
 * WHAT CAN CHANGE, AND WHEN (the API decides; this draws the buttons):
 *   • lines, Rebuild and the paper sign-off — while a DRAFT only. Once signed
 *     the table is read only and no edit control is drawn;
 *   • Approve — admin and office staff only (the API's EW_ADMIN), and only a
 *     SIGNED ticket;
 *   • Void — any status but void, until the ticket is on an invoice;
 *   • "Attach customer-signed copy" — ANY status, after approval and after
 *     invoicing included. It never changes a line or the status;
 *   • internal notes — any status.
 *
 * Every file is an `ew_job` upload presigned against the ticket's JOB
 * (uploadEwJobFile: presign → PUT, `res.ok` checked → confirm → upload id).
 * Figures are the API's; the subtotal is what the customer signed, or on a
 * draft the priced lines so far, labelled "so far".
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Activity, CheckCircle2, FileText, ListChecks, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useEwMeta } from '../../hooks/useEarthworks';
import { uploadEwJobFile } from '../../hooks/useEwField';
import {
  useAddEwTicketLine,
  useApproveEwTicket,
  useAttachEwSignedCopy,
  useEwTicket,
  usePaperSignEwTicket,
  useRebuildEwTicket,
  useRemoveEwTicketLine,
  useUpdateEwTicketLine,
  useUpdateEwTicketNotes,
  useVoidEwTicket,
} from '../../hooks/useEwTickets';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ewDay, ewInstant, ewRefusal } from '../../lib/earthworks';
import {
  canApproveEwTicket,
  canWorkEwTickets,
  ewFileKind,
  ewLineSource,
  ewSignedVia,
  ewTicketApprovable,
  ewTicketEditable,
  ewTicketSkin,
  ewTicketVoidable,
} from '../../lib/ew-tickets';
import type { EwTicketDetail } from '../../types/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { EwFilePick, EwLinesPanel, type EwLineRow } from './EwLinesPanel';
import { ewTicketMoney } from './EwTicketsScreen';
import {
  isNotFound,
  EW_EYEBROW,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwBadge,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwLoading,
  EwPanel,
  EwSpec,
} from './EwBits';

export const ticketLineRows = (t: EwTicketDetail): EwLineRow[] =>
  t.lines.map((l) => ({
    id: l.id,
    kind: l.kind,
    kindLabel: l.kindLabel,
    description: l.description,
    unit: l.unit,
    unitLabel: l.unitLabel,
    quantity: l.quantity,
    rate: l.rate,
    amount: l.amount,
    unpriced: l.unpriced,
    unpricedReason: l.unpricedReason,
    note: [ewLineSource(l.source), l.workDate ? ewDay(l.workDate) : null, l.standby ? 'Standby' : null].filter((x) => x && x !== '—').join(' · ') || null,
    locked: l.source === 'change_order',
  }));

export default function EwTicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList('/earthworks/tickets');
  const { user } = useAuth();
  const role = user?.role;
  const canWork = canWorkEwTickets(role);
  const canApprove = canApproveEwTicket(role);
  const tz = useTenantTimeZone();
  const query = useEwTicket(id);
  const metaQ = useEwMeta();
  const catalog = metaQ.data?.data ?? null;
  const meta = metaQ.data?.meta ?? null;

  const addLine = useAddEwTicketLine();
  const updateLine = useUpdateEwTicketLine();
  const removeLine = useRemoveEwTicketLine();
  const rebuild = useRebuildEwTicket();
  const approve = useApproveEwTicket();
  const voidT = useVoidEwTicket();
  const paper = usePaperSignEwTicket();
  const signedCopy = useAttachEwSignedCopy();
  const notesM = useUpdateEwTicketNotes();

  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [paperName, setPaperName] = useState('');
  const [paperTitle, setPaperTitle] = useState('');
  const [paperBusy, setPaperBusy] = useState(false);
  const [paperError, setPaperError] = useState<string | null>(null);
  const [copyNotes, setCopyNotes] = useState('');
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState<string | null>(null);

  const t = query.data ?? null;
  useEffect(() => { setNotes(t?.notes ?? ''); }, [t?.id, t?.notes]);
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const editable = canWork && !!t && ewTicketEditable(t.status);
  const money = t ? ewTicketMoney(t) : null;
  const linesPending = addLine.isPending || updateLine.isPending || removeLine.isPending;

  const doApprove = () => {
    if (!t) return;
    setDialogError(null);
    approve.mutate(t.id, {
      onSuccess: () => { toast.success(`${t.ticketNumber} approved`); setApproving(false); },
      onError: (err) => setDialogError(ewRefusal(err, 'The ticket could not be approved.')),
    });
  };
  const doVoid = () => {
    if (!t) return;
    setDialogError(null);
    if (!voidReason.trim()) return setDialogError('Say why the ticket is void.');
    voidT.mutate({ id: t.id, reason: voidReason.trim() }, {
      onSuccess: () => { toast.success(`${t.ticketNumber} voided`); setVoiding(false); setVoidReason(''); },
      onError: (err) => setDialogError(ewRefusal(err, 'The ticket could not be voided.')),
    });
  };
  const doRebuild = () => {
    if (!t) return;
    setActionError(null);
    rebuild.mutate(t.id, {
      onSuccess: (r) => {
        toast.success(r.built ? `Rebuilt: ${r.built.drafted} line${r.built.drafted === 1 ? '' : 's'} from ${r.built.logs} log${r.built.logs === 1 ? '' : 's'}` : 'Rebuilt');
        if (r.built?.flags?.length) toast.warning(r.built.flags.join(' '), { duration: 15000 });
      },
      onError: (err) => setActionError(ewRefusal(err, 'The ticket could not be rebuilt.')),
    });
  };
  const doPaper = async (file: File) => {
    if (!t) return;
    setPaperError(null);
    setPaperBusy(true);
    try {
      const uploadId = await uploadEwJobFile(t.jobId, file);
      await paper.mutateAsync({ id: t.id, body: { upload_id: uploadId, signer_name: paperName.trim() || undefined, signer_title: paperTitle.trim() || undefined } });
      toast.success(`${t.ticketNumber} signed on paper`);
    } catch (err) {
      setPaperError(err instanceof Error && !(err as { response?: unknown }).response ? err.message : ewRefusal(err, 'The signed ticket could not be attached.'));
    } finally {
      setPaperBusy(false);
    }
  };
  const doCopy = async (file: File) => {
    if (!t) return;
    setCopyError(null);
    setCopyBusy(true);
    try {
      const uploadId = await uploadEwJobFile(t.jobId, file);
      await signedCopy.mutateAsync({ id: t.id, body: { upload_id: uploadId, notes: copyNotes.trim() || undefined } });
      setCopyNotes('');
      toast.success('Signed copy attached');
    } catch (err) {
      setCopyError(err instanceof Error && !(err as { response?: unknown }).response ? err.message : ewRefusal(err, 'The signed copy could not be attached.'));
    } finally {
      setCopyBusy(false);
    }
  };
  const saveNotes = () => {
    if (!t) return;
    setNotesError(null);
    notesM.mutate({ id: t.id, notes: notes.trim() || null }, {
      onSuccess: () => toast.success('Notes saved'),
      onError: (err) => setNotesError(ewRefusal(err, 'The notes could not be saved.')),
    });
  };

  return (
    <div data-testid="ew-ticket-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${EW_EYEBROW} · Field ticket`}
        title={t?.ticketNumber || (query.isLoading ? 'Ticket' : '—')}
        description={t ? `${t.jobNumber} · ${t.customerName ?? '—'} · ${t.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          t && canWork ? (
            <div className="flex flex-wrap gap-2" data-testid="ew-ticket-actions">
              {canApprove && ewTicketApprovable(t.status) && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setDialogError(null); setApproving(true); }} data-testid="ew-ticket-approve">
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  Approve
                </button>
              )}
              {editable && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={doRebuild} disabled={rebuild.isPending} data-testid="ew-ticket-rebuild">
                  <RefreshCw className="size-4" aria-hidden="true" />
                  {rebuild.isPending ? 'Rebuilding…' : 'Rebuild from logs'}
                </button>
              )}
              {ewTicketVoidable(t) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDialogError(null); setVoiding(true); }} data-testid="ew-ticket-void">
                  <XCircle className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: t ? ewTicketSkin(t.status).label : '—' },
          { icon: <ListChecks className="size-4" aria-hidden="true" />, label: 'Lines', tone: 'neutral', loading: query.isLoading, value: t ? String(t.lineCount) : '—', sub: t && t.unpricedCount > 0 ? `${t.unpricedCount} unpriced` : undefined },
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Subtotal', tone: 'neutral', loading: query.isLoading, value: money?.value ?? '—', sub: money?.note && money.value !== '—' ? money.note : undefined },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canWork ? (
            <EwPanel eyebrow="Field ticket" title="Ticket">
              <EwEmpty>Field tickets carry prices, so they are for the office, supervisors and dispatch. Your tickets are on each job's screen.</EwEmpty>
            </EwPanel>
          ) : query.isLoading ? (
            <EwLoading label="Loading ticket" />
          ) : notFound || (!failure && !t) ? (
            <EwPanel eyebrow="Field ticket" title="Ticket">
              <EwEmpty>This ticket was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : failure || !t ? (
            <EwPanel eyebrow="Field ticket" title="Ticket">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this ticket" />
            </EwPanel>
          ) : (
            <>
              <EwFormError message={actionError} />
              {t.status === 'void' && (
                <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-ticket-void-note">
                  Void{t.voidedByName ? ` by ${t.voidedByName}` : ''}{t.voidedAt ? ` · ${ewInstant(t.voidedAt, tz)}` : ''}{t.voidReason ? ` — ${t.voidReason}` : ''}
                </p>
              )}
              {t.invoicedAt && (
                <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-ticket-invoiced">
                  On an invoice since {ewInstant(t.invoicedAt, tz)}. It can no longer be voided; a customer-signed copy can still be attached.
                </p>
              )}

              <EwPanel eyebrow="Field ticket" title="Details" testId="ew-ticket-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <EwSpec label="Status" value={<EwBadge skin={ewTicketSkin(t.status)} value={t.status} />} />
                  <EwSpec label="Job" value={<Link className="font-bold text-fi-amber underline" to={`/earthworks/jobs/${t.jobId}`}>{t.jobNumber}</Link>} />
                  <EwSpec label="Customer" value={t.customerName || '—'} />
                  <EwSpec label="Site" value={t.siteName || '—'} />
                  <EwSpec label="Ticketing" value={t.modeLabel || t.mode || '—'} />
                  <EwSpec label="Work day" value={t.mode === 'per_day' ? ewDay(t.workDate) : '—'} />
                  <EwSpec label="Currency" value={t.currency || '—'} />
                  <EwSpec label="Created" value={ewInstant(t.createdAt, tz)} />
                </dl>
              </EwPanel>

              <EwLinesPanel
                eyebrow="Ticket"
                title="Lines"
                testId="ew-ticket-lines"
                lines={ticketLineRows(t)}
                currency={t.currency}
                editable={editable}
                catalog={catalog}
                meta={meta}
                total={t.status === 'draft' ? t.draftSubtotal : t.subtotal}
                totalLabel={t.status === 'draft' ? 'Priced so far' : 'Signed subtotal'}
                pending={linesPending}
                onAdd={(body) => addLine.mutateAsync({ id: t.id, body })}
                onUpdate={(lineId, body) => updateLine.mutateAsync({ id: t.id, lineId, body })}
                onRemove={(lineId) => removeLine.mutateAsync({ id: t.id, lineId })}
              />

              <EwPanel eyebrow="Customer" title="Sign-off" testId="ew-ticket-signoff">
                {t.status === 'draft' ? (
                  <div className="space-y-3 px-4 py-4">
                    <p className="text-sm text-fieldiq-text">
                      The customer signs on the crew's phone, from the job's field screen. If they signed a paper ticket instead, attach a photo of it here — that signs the ticket.
                    </p>
                    <EwFormError message={paperError} />
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <EwField label="Signed by (optional)">
                        <input className={EW_INPUT} aria-label="Paper signer name" value={paperName} onChange={(e) => setPaperName(e.target.value)} />
                      </EwField>
                      <EwField label="Their title (optional)">
                        <input className={EW_INPUT} aria-label="Paper signer title" value={paperTitle} onChange={(e) => setPaperTitle(e.target.value)} />
                      </EwField>
                    </div>
                    <EwFilePick label={paperBusy ? 'Attaching…' : 'Attach signed paper ticket'} onPick={doPaper} disabled={paperBusy} testId="ew-ticket-paper-input" />
                  </div>
                ) : (
                  <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="ew-ticket-signed">
                    <EwSpec label="Signed" value={t.signedVia ? ewSignedVia(t.signedVia) : '—'} />
                    <EwSpec label="Signer" value={t.signerName ? `${t.signerName}${t.signerTitle ? `, ${t.signerTitle}` : ''}` : '—'} />
                    <EwSpec label="Signed at" value={ewInstant(t.signedAt, tz)} />
                    <EwSpec label="Approved" value={t.approvedAt ? `${t.approvedByName ?? '—'} · ${ewInstant(t.approvedAt, tz)}` : '—'} />
                    {t.signatureUrl && (
                      <div className="sm:col-span-2">
                        <img src={t.signatureUrl} alt="Customer signature" className="h-20 rounded bg-white p-1" />
                      </div>
                    )}
                  </dl>
                )}
              </EwPanel>

              <EwPanel eyebrow="Files" title="Signed copies and photos" testId="ew-ticket-files" footer={t.files.length ? `${t.files.length} file${t.files.length === 1 ? '' : 's'}` : undefined}>
                <div className="space-y-3 px-4 py-4" data-testid="ew-ticket-signed-copy">
                  <p className="text-sm text-fieldiq-text">A copy the customer signed later can be attached at any time, after approval or invoicing too. It does not change the ticket.</p>
                  <EwFormError message={copyError} />
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-80">
                      <EwField label="Note (optional)">
                        <input className={EW_INPUT} aria-label="Signed copy note" value={copyNotes} onChange={(e) => setCopyNotes(e.target.value)} />
                      </EwField>
                    </div>
                    <EwFilePick label={copyBusy ? 'Attaching…' : 'Attach customer-signed copy'} onPick={doCopy} disabled={copyBusy} testId="ew-ticket-copy-input" />
                  </div>
                </div>
                {t.files.length === 0 ? (
                  <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">No files on this ticket.</p>
                ) : (
                  <ul className="border-t border-fieldiq-border">
                    {t.files.map((f) => (
                      <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-2.5 text-sm last:border-b-0" data-testid="ew-ticket-file">
                        <span className="min-w-0">
                          <span className="font-bold text-white">{ewFileKind(f.kind)}</span>
                          <span className="block text-xs text-fieldiq-muted">
                            {f.fileName || '—'} · {f.createdByName || '—'} · {ewInstant(f.createdAt, tz)}{f.notes ? ` — ${f.notes}` : ''}
                          </span>
                        </span>
                        {f.url && <a href={f.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-fi-amber underline">Open</a>}
                      </li>
                    ))}
                  </ul>
                )}
              </EwPanel>

              <EwPanel eyebrow="Built from" title="Daily logs" testId="ew-ticket-logs">
                {t.dailyLogs.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-fieldiq-muted">No daily logs are on this ticket.</p>
                ) : (
                  <ul>
                    {t.dailyLogs.map((l) => (
                      <li key={l.dailyLogId} className="flex items-center justify-between border-b border-fieldiq-border px-4 py-2.5 text-sm last:border-b-0">
                        <Link className="font-bold text-fi-amber underline" to={`/earthworks/daily-logs/${l.dailyLogId}`}>{ewDay(l.workDate)}</Link>
                        {!l.live && <span className="text-xs text-fieldiq-muted">Released (ticket void)</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </EwPanel>

              <EwPanel eyebrow="Internal" title="Notes" testId="ew-ticket-notes">
                <div className="space-y-3 px-4 py-4">
                  <EwFormError message={notesError} />
                  <textarea className={`${EW_INPUT} h-24 py-2`} aria-label="Ticket notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <div className="flex justify-end">
                    <button type="button" className={EW_SECONDARY} onClick={saveNotes} disabled={notesM.isPending || notes === (t.notes ?? '')}>
                      {notesM.isPending ? 'Saving…' : 'Save notes'}
                    </button>
                  </div>
                </div>
              </EwPanel>
            </>
          )}
        </div>
      </section>

      {t && (
        <>
          <EwDialog
            open={approving}
            title={`Approve ${t.ticketNumber}`}
            onClose={() => setApproving(false)}
            footer={
              <>
                <button type="button" className={EW_SECONDARY} onClick={() => setApproving(false)} disabled={approve.isPending}>Not now</button>
                <button type="button" className={EW_PRIMARY} onClick={doApprove} disabled={approve.isPending}>{approve.isPending ? 'Approving…' : 'Approve'}</button>
              </>
            }
          >
            <EwFormError message={dialogError} />
            <p className="text-sm text-fieldiq-text">Approves the signed ticket for invoicing at {money?.value ?? '—'}.</p>
          </EwDialog>
          <EwDialog
            open={voiding}
            title={`Void ${t.ticketNumber}`}
            onClose={() => setVoiding(false)}
            footer={
              <>
                <button type="button" className={EW_SECONDARY} onClick={() => setVoiding(false)} disabled={voidT.isPending}>Keep ticket</button>
                <button type="button" className={EW_PRIMARY} onClick={doVoid} disabled={voidT.isPending}>{voidT.isPending ? 'Voiding…' : 'Void ticket'}</button>
              </>
            }
          >
            <EwFormError message={dialogError} />
            <p className="text-sm text-fieldiq-text">A void ticket releases its daily logs and change-order lines, so a new ticket can carry them.</p>
            <div className="mt-3">
              <EwField label="Reason">
                <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Void reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
              </EwField>
            </div>
          </EwDialog>
        </>
      )}
    </div>
  );
}
